import { supabase, getUserRole } from '../config/supabase.js'
import { 
  extractYear, 
  formatEventDate, 
  getPrimaryName, 
  getLifeDates 
} from '../utils/helpers.js'
import { navigateToPerson } from '../router/router.js'
import './person-detail.css'

export async function loadPersonDetail(personId) {
  const contentDiv = document.getElementById('person-detail-content')
  contentDiv.innerHTML = '<div class="loading">Loading person details...</div>'
  
  try {
    const userRole = await getUserRole()
    
    // Fetch person with all related data
    const { data: person, error: personError } = await supabase
      .from('person')
      .select(`
        *,
        person_name (*),
        event (*)
      `)
      .eq('id', personId)
      .single()
    
    if (personError) throw personError
    
    // Fetch family relationships where person is a partner
    const { data: familiesAsPartner, error: familyError } = await supabase
      .from('family')
      .select(`
        *,
        partner1:person!family_partner1_id_fkey (
          id,
          is_living,
          person_name (full_name, is_primary)
        ),
        partner2:person!family_partner2_id_fkey (
          id,
          is_living,
          person_name (full_name, is_primary)
        ),
        family_child (
          child:person (
            id,
            is_living,
            person_name (full_name, is_primary),
            event (event_type, date_text, date_sort)
          )
        )
      `)
      .or(`partner1_id.eq.${personId},partner2_id.eq.${personId}`)
    
    if (familyError) throw familyError
    
    // Fetch families where person is a child
    const { data: familiesAsChild, error: childError } = await supabase
      .from('family_child')
      .select(`
        family:family (
          id,
          partner1:person!family_partner1_id_fkey (
            id,
            is_living,
            person_name (full_name, is_primary),
            event (event_type, date_text, date_sort)
          ),
          partner2:person!family_partner2_id_fkey (
            id,
            is_living,
            person_name (full_name, is_primary),
            event (event_type, date_text, date_sort)
          ),
          family_child (
            child:person (
              id,
              is_living,
              person_name (full_name, is_primary)
            )
          )
        )
      `)
      .eq('child_id', personId)
    
    if (childError) throw childError
    
    renderPersonDetail(person, familiesAsPartner, familiesAsChild, userRole)
    
  } catch (error) {
    console.error('Error loading person:', error)
    contentDiv.innerHTML = `
      <div class="error">
        <strong>Error loading person details:</strong> ${error.message}
      </div>
    `
  }
}

function renderFamilyTreeCards(person, familiesAsPartner, familiesAsChild) {
  // Check if there are any relationships
  const hasParents = familiesAsChild && familiesAsChild.length > 0 && 
    familiesAsChild.some(fc => fc.family.partner1 || fc.family.partner2)
  const hasSiblings = familiesAsChild && familiesAsChild.length > 0 && 
    familiesAsChild.some(fc => fc.family.family_child && fc.family.family_child.length > 1)
  const hasSpouses = familiesAsPartner && familiesAsPartner.length > 0
  
  if (!hasParents && !hasSiblings && !hasSpouses) {
    return '<div class="no-data">No family relationships recorded</div>'
  }
  
  const primaryName = person.person_name.find(n => n.is_primary) || person.person_name[0]
  let html = '<div class="family-tree-cards">'
  
  // Parents section
  if (hasParents) {
    html += '<div class="family-section-header">Parents</div>'
    html += '<div class="family-group">'
    
    familiesAsChild.forEach(fc => {
      const family = fc.family
      if (family.partner1) {
        const lifeDates = getLifeDates(family.partner1)
        html += `
          <div class="family-card ${!family.partner1.is_living ? 'deceased' : ''}" data-person-id="${family.partner1.id}">
            <div class="family-card-info">
              <div class="family-card-name">${getPrimaryName(family.partner1.person_name)}</div>
              ${lifeDates ? `<div class="family-card-years">${lifeDates}</div>` : ''}
            </div>
            <div class="family-badge family-badge-parent">Parent</div>
          </div>
        `
      }
      if (family.partner2) {
        const lifeDates = getLifeDates(family.partner2)
        html += `
          <div class="family-card ${!family.partner2.is_living ? 'deceased' : ''}" data-person-id="${family.partner2.id}">
            <div class="family-card-info">
              <div class="family-card-name">${getPrimaryName(family.partner2.person_name)}</div>
              ${lifeDates ? `<div class="family-card-years">${lifeDates}</div>` : ''}
            </div>
            <div class="family-badge family-badge-parent">Parent</div>
          </div>
        `
      }
    })
    
    html += '</div>' // end family-group
  }
  
  // Siblings section
  if (hasSiblings) {
    html += '<div class="family-section-header">Siblings</div>'
    html += '<div class="family-group">'
    
    const siblingsAdded = new Set() // Avoid duplicates
    familiesAsChild.forEach(fc => {
      const family = fc.family
      if (family.family_child) {
        family.family_child.forEach(fc2 => {
          if (fc2.child.id !== person.id && !siblingsAdded.has(fc2.child.id)) {
            siblingsAdded.add(fc2.child.id)
            html += `
              <div class="family-card ${!fc2.child.is_living ? 'deceased' : ''}" data-person-id="${fc2.child.id}">
                <div class="family-card-info">
                  <div class="family-card-name">${getPrimaryName(fc2.child.person_name)}</div>
                </div>
                <div class="family-badge family-badge-sibling">Sibling</div>
              </div>
            `
          }
        })
      }
    })
    
    html += '</div>' // end family-group
  }
  
  // Current person section
  html += `
    <div class="current-person-section">
      <div class="current-person-label">You are viewing</div>
      <div class="family-card current" data-person-id="${person.id}">
        <div class="family-card-info">
          <div class="family-card-name">${primaryName.full_name}</div>
          <div class="family-card-years">${person.is_living ? 'Living' : 'Deceased'}</div>
        </div>
      </div>
    </div>
  `
  
  // Spouses and children (grouped by marriage)
  if (hasSpouses) {
    familiesAsPartner.forEach((family, index) => {
      const spouse = family.partner1_id === person.id ? family.partner2 : family.partner1
      const hasChildren = family.family_child && family.family_child.length > 0
      
      html += '<div class="marriage-group">'
      
      // Marriage header (only if multiple marriages)
      if (familiesAsPartner.length > 1) {
        const marriageNum = index + 1
        const ordinal = marriageNum === 1 ? '1st' : marriageNum === 2 ? '2nd' : marriageNum === 3 ? '3rd' : `${marriageNum}th`
        html += `<div class="marriage-group-header"><span class="marriage-number">${ordinal}</span>Marriage</div>`
      } else {
        html += '<div class="family-section-header">Spouse & Children</div>'
      }
      
      // Spouse card
      if (spouse) {
        const lifeDates = getLifeDates(spouse)
        const isDeceased = !spouse.is_living
        html += `
          <div class="family-card ${isDeceased ? 'deceased' : ''}" data-person-id="${spouse.id}">
            <div class="family-card-info">
              <div class="family-card-name">${getPrimaryName(spouse.person_name)}</div>
              ${lifeDates ? `<div class="family-card-years">${lifeDates}</div>` : ''}
            </div>
            <div class="family-badge family-badge-spouse">Spouse</div>
          </div>
        `
      }
      
      // Children from this marriage
      if (hasChildren) {
        html += '<div class="children-group">'
        html += '<div class="children-label">Children from this relationship</div>'
        
        family.family_child.forEach(fc => {
          if (fc.child) {
            const birthEvent = fc.child.event?.find(e => e.event_type === 'birth')
            const birthYear = birthEvent ? extractYear(birthEvent.date_text) : ''
            html += `
              <div class="family-card ${!fc.child.is_living ? 'deceased' : ''}" data-person-id="${fc.child.id}">
                <div class="family-card-info">
                  <div class="family-card-name">${getPrimaryName(fc.child.person_name)}</div>
                  ${birthYear ? `<div class="family-card-years">b. ${birthYear}</div>` : ''}
                </div>
                <div class="family-badge family-badge-child">Child</div>
              </div>
            `
          }
        })
        
        html += '</div>' // end children-group
      }
      
      html += '</div>' // end marriage-group
    })
  }
  
  html += '</div>' // end family-tree-cards
  return html
}

function renderPersonDetail(person, familiesAsPartner, familiesAsChild, userRole) {
  const primaryName = person.person_name.find(n => n.is_primary) || person.person_name[0]
  const birthEvent = person.event.find(e => e.event_type === 'birth')
  const deathEvent = person.event.find(e => e.event_type === 'death')
  
  // Calculate lifespan
  let lifespan = ''
  if (birthEvent) {
    const birthYear = extractYear(birthEvent.date_text)
    if (person.is_living) {
      lifespan = `${birthYear || '?'} - Present`
    } else if (deathEvent) {
      const deathYear = extractYear(deathEvent.date_text)
      lifespan = `${birthYear || '?'} - ${deathYear || '?'}`
    } else {
      lifespan = `${birthYear || '?'} - ?`
    }
  } else {
    lifespan = person.is_living ? 'Living' : 'Deceased'
  }
  
  // Sort events by date
  const sortedEvents = [...person.event].sort((a, b) => {
    if (!a.date_sort) return 1
    if (!b.date_sort) return -1
    return new Date(a.date_sort) - new Date(b.date_sort)
  })
  
  // Build HTML
  let html = `
    <div class="person-detail-header">
      <h2>
        ${primaryName.full_name}
        <span class="badge ${person.is_living ? 'badge-living' : 'badge-deceased'}" style="font-size: 16px; vertical-align: middle;">
          ${person.is_living ? 'Living' : 'Deceased'}
        </span>
      </h2>
      <div class="person-lifespan">${lifespan}</div>
    </div>
    
    <div class="detail-section">
      <h3>Basic Information</h3>
      <div class="info-grid">
        ${person.person_name.length > 1 ? `
          <div class="info-item">
            <div class="info-label">All Known Names</div>
            <div class="info-value">
              ${person.person_name.map(n => 
                `${n.full_name}${n.is_primary ? ' (Primary)' : ''}`
              ).join('<br>')}
            </div>
          </div>
        ` : ''}
        
        ${person.sex ? `
          <div class="info-item">
            <div class="info-label">Gender</div>
            <div class="info-value">${person.sex === 'M' ? 'Male' : person.sex === 'F' ? 'Female' : 'Unknown'}</div>
          </div>
        ` : ''}
        
        <div class="info-item">
          <div class="info-label">Privacy Level</div>
          <div class="info-value" style="text-transform: capitalize;">${person.privacy_level}</div>
        </div>
      </div>
    </div>
    
    <div class="detail-section">
      <h3>Life Events</h3>
      ${sortedEvents.length > 0 ? `
        ${sortedEvents.map(event => `
          <div class="timeline-event">
            <div class="timeline-event-type">${event.event_type}</div>
            <div class="timeline-event-date">
              ${formatEventDate(event, person.is_living, userRole)}
            </div>
            ${event.place_text ? `
              <div class="timeline-event-place">ðŸ“ ${event.place_text}</div>
            ` : ''}
            ${event.description ? `
              <div class="timeline-event-description">${event.description}</div>
            ` : ''}
          </div>
        `).join('')}
      ` : '<div class="no-data">No events recorded</div>'}
    </div>
    
    <div class="detail-section">
      <h3>Family Relationships</h3>
      
      ${renderFamilyTreeCards(person, familiesAsPartner, familiesAsChild)}
    </div>
    
    ${person.notes ? `
      <div class="detail-section">
        <h3>Notes</h3>
        <div style="color: #333; line-height: 1.6;">${person.notes}</div>
      </div>
    ` : ''}
    
    ${userRole === 'admin' ? `
      <div class="detail-section admin-section">
        <h3>Admin Information</h3>
        <div class="info-grid">
          ${person.gedcom_xref ? `
            <div class="info-item">
              <div class="info-label">GEDCOM ID</div>
              <div class="info-value">${person.gedcom_xref}</div>
            </div>
          ` : ''}
          <div class="info-item">
            <div class="info-label">Database ID</div>
            <div class="info-value" style="font-family: monospace; font-size: 12px;">${person.id}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Created</div>
            <div class="info-value">${new Date(person.created_at).toLocaleDateString()}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Last Updated</div>
            <div class="info-value">${new Date(person.updated_at).toLocaleDateString()}</div>
          </div>
        </div>
        <button class="edit-btn" id="edit-person-btn">
          Edit Person
        </button>
      </div>
    ` : ''}
  `
  
  document.getElementById('person-detail-content').innerHTML = html
  
  // Add click handlers to family tree cards
  document.querySelectorAll('.family-card:not(.current)').forEach(card => {
    card.addEventListener('click', () => {
      const personId = card.dataset.personId
      if (personId) {
        navigateToPerson(personId)
      }
    })
  })

  // Add edit button handler for admins
  const editBtn = document.getElementById('edit-person-btn')
  if (editBtn) {
    editBtn.addEventListener('click', () => {
      import('../router/router.js').then(({ navigateToPersonEdit }) => {
        navigateToPersonEdit(person.id)
      })
    })
  }

}

export function showPersonDetail() {
  document.getElementById('content').classList.add('hidden')
  document.getElementById('person-edit-page').classList.add('hidden')
  document.getElementById('person-add-page').classList.add('hidden')
  document.getElementById('password-setup-page').classList.add('hidden')
  document.getElementById('user-profile-page').classList.add('hidden')  // ADD THIS
  document.getElementById('person-detail-page').classList.remove('hidden')
}
